import {
  CHARACTER_VISIBILITY,
  CharacterFlow,
  useLoadCharacter,
  useTranslation,
  useUserInfo,
  useUserSettings,
} from "@next-platform/client-common-core";
import { CapabilityGate, SubCapabilityKey } from "@next-platform/client-common-core/infra/application/capabilities";
import basicAppConfig from "@next-platform/client-common-core/infra/constants/basicAppConfig";
import getCharacterLorebookProfileIndicator from "@next-platform/client-common-core/lorebook/getCharacterLorebookProfileIndicator";
import {
  Alert,
  AlertVariant,
  AvatarChip,
  Button,
  ButtonSize,
  ButtonVariant,
  Icon,
  IconSize,
  IconVariants,
  Image,
  Text,
  TextBox,
  TextVariants,
  useMedia,
} from "@next-platform/client-common-spicy-ui";
import { LorebookVisibility } from "@next-platform/packages-ts-types";
import { Spinner } from "@nextui-org/react";
import TagSuggestion from "@src/components/TagSuggestion/TagSuggestion";
import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

import { CapabilityShareCharacterModal, Col, LocalizedLink, Row } from "@/components";
import { CharacterTranslation } from "@/components/CharacterTranslation/CharacterTranslation";
import AvatarWithModal from "@/components/common/AvatarWithModal";
import NavigateBackButtonV2 from "@/components/common/NavigateBackButtonV2";
import PageStaticMetaTags from "@/components/common/PageStaticMetaTags";
import Trans from "@/components/Trans/Trans";
import { EXTERNAL_ROUTES_FOR_APPLICATION, FEATURE_FLAGS } from "@/constants";
import { MINIMUM_PARTICIPANT_LIMIT } from "@/constants/groupChat";
import { useFeatureFlag } from "@/hooks";
import useLocalizedLocation from "@/hooks/useLocalizedLocation";
import { useRecombee } from "@/hooks/useRecombee";
import { AvatarPreset } from "@/types";
import { cn, formatNumber, getAvatarUrl, getBusinessRouteScreen, getIsPartner, getProfilePathForCharacter } from "@/utils";

const RATING_PERCENT_MULTIPLIER = 100;

export default function GroupchatProfile() {
  const { t } = useTranslation();
  const applicationId = basicAppConfig.applicationId;
  const { pagePath } = useLocalizedLocation();
  const screenName = getBusinessRouteScreen(pagePath);
  const { onlyUserId, isAuthenticated } = useUserInfo();
  const { blurNSFW, likedBots, onUserLikebot } = useUserSettings();
  const { isMobile, isDesktop } = useMedia();
  const { characterId } = useParams();
  const navigate = useNavigate();
  const { sendUserInteractionAddBookmark, recombeeUserId } = useRecombee();
  const { characterData: character, loadingType: characterLoadingType } = useLoadCharacter({ characterId: characterId ?? "", withTranslation: true });
  const { hasFeatureFlag: hasGroupLorebookFeatureFlag } = useFeatureFlag(FEATURE_FLAGS.RELEASE_GROUP_LOREBOOK_ATTACHMENT_WEB);
  const [stateIsFavoriteLoading, setStateIsFavoriteLoading] = useState(false);
  const [stateIsFavorite, setStateIsFavorite] = useState(false);
  const [stateIsShareModalOpen, setStateIsShareModalOpen] = useState(false);
  const isPartner = getIsPartner();
  const [stateIsZoomedAvatarModalOpen, setStateIsZoomedAvatarModalOpen] = useState(false);
  const isAvatarBlurred = (!isAuthenticated || blurNSFW) && character?.avatar_is_nsfw;
  const showMoreLabel = t("chatbot:profile.showMore");
  const showLessLabel = t("chatbot:profile.showLess");
  const isPageLoading = !character || characterLoadingType === CharacterFlow.GET_CHARACTER;

  //This will resolve to true if currentChat is not a groupChat or has no enough members
  const isInvalidChat = character?.type === "META" && (character?.sub_characters?.length ?? 0) < MINIMUM_PARTICIPANT_LIMIT;

  useEffect(() => {
    if (!!characterId && !likedBots?.includes(characterId)) {
      setStateIsFavorite(false);
    } else {
      setStateIsFavorite(true);
    }
  }, [likedBots]);

  const metaProperties = useMemo(() => ({ characterName: character?.name ?? undefined }), [character?.name]);

  if (isPageLoading) {
    return <Spinner />;
  }

  // If definition visibility is false then only name, title, creator name & Tags should be visible.
  const isPageNotAccessible = character?.definition_visible !== true && character?.creator_user_id !== onlyUserId;

  const isGroupCreator = !!onlyUserId && character?.creator_user_id === onlyUserId;
  const lorebookIndicator = getCharacterLorebookProfileIndicator({ character, isCreator: isGroupCreator });
  const hasLorebookDisplayed = hasGroupLorebookFeatureFlag && lorebookIndicator;
  const displayedLorebook =
    character?.lorebooks?.find((entry) => isGroupCreator || entry?.visibility === LorebookVisibility.PUBLIC) ?? character?.lorebooks?.[0];
  const lorebookIndicatorIcon = displayedLorebook?.visibility === LorebookVisibility.PUBLIC ? IconVariants.BOOK_TEXT : IconVariants.BOOK_LOCK;

  const handleFavorite = () => {
    if (!characterId) {
      return;
    }
    setStateIsFavoriteLoading(true);
    onUserLikebot(characterId, screenName)
      .then(() => {
        if (stateIsFavorite) {
          return sendUserInteractionAddBookmark(recombeeUserId, characterId);
        }
        return undefined;
      })
      .catch(() => {
        // Favorite toggle failed silently; loading state is reset in finally.
      })
      .finally(() => {
        setStateIsFavoriteLoading(false);
      });
  };

  return (
    <Row className="w-full flex flex-col max-w-[700px] gap-lg mx-auto">
      <PageStaticMetaTags
        pageId="characterDefinition"
        properties={metaProperties}
        ogImage={
          getAvatarUrl(character?.avatar_url || "", {
            preset: AvatarPreset.IMAGE_500X650,
          }) || undefined
        }
        jsonLdTokens={{
          webPageName: character?.name ?? "",
          webPageDescription: character?.title ?? "",
          webPageAuthor: character?.creator_username ?? "",
          webPageMainEntity: character?.name ?? "",
        }}
      />
      <Row>
        <Col>
          <NavigateBackButtonV2 />
        </Col>
        <Col className="max-w-[80%]">
          <CapabilityGate capability={SubCapabilityKey.CharacterTranslate}>
            <CharacterTranslation
              characterId={characterId}
              style="profile-v2"
              characterLanguage={character?.language}
              characterVisibility={character?.visibility}
            />
          </CapabilityGate>
        </Col>
      </Row>
      <Alert
        variant={AlertVariant.ERROR}
        isDismissable={false}
        visible={isInvalidChat}
        title={t("chatbot:groupProfile.alert.unavailable.title")}
        description={t("chatbot:groupProfile.alert.unavailable.description")}
      />
      <Row className="gap-5 flex-col sm:flex-row">
        <AvatarWithModal
          src={character?.avatar_url || ""}
          isOpen={stateIsZoomedAvatarModalOpen}
          onOpenChange={setStateIsZoomedAvatarModalOpen}
          blurred={isAvatarBlurred}
        >
          <Image
            src={
              getAvatarUrl(character?.avatar_url || "", {
                preset: isMobile ? AvatarPreset.AVATAR_400X400 : AvatarPreset.AVATAR_640X640,
              }) || undefined
            }
            alt="avatar image"
            className={`cursor-pointer w-full aspect-[3/4] mob:w-[183px] mob:h-[244px] object-cover rounded-md ${isAvatarBlurred ? "blur sm:blur-lg" : "blur-none"}
            `}
            onClick={() => setStateIsZoomedAvatarModalOpen(true)}
          />
        </AvatarWithModal>

        <Row className="flex-col gap-5">
          <Col className="gap-1">
            <Text variant={TextVariants.MOBILE_HEADING_3_BOLD}>{character?.name}</Text>
            <LocalizedLink className="text-link" to={`/creator/${character?.creator_username}`} aria-label="creator-profile">
              <Text variant={TextVariants.LABEL_LARGE_REGULAR}> @{character?.creator_username}</Text>
            </LocalizedLink>
          </Col>

          <Row className="flex-wrap gap-1">
            <LocalizedLink to={`/chat/${characterId}`} aria-label={`chat-with-${character.name}`}>
              <Button disabled={isInvalidChat} iconStart={IconVariants.MESSAGE_MORE} aria-label="chat-now">
                <Trans i18nKey="common:chatNow" />
              </Button>
            </LocalizedLink>
            {character?.visibility === CHARACTER_VISIBILITY.PUBLIC && (
              <Button
                iconStart={IconVariants.FAVORITE}
                variant={ButtonVariant.TEXT}
                onClick={handleFavorite}
                loading={stateIsFavoriteLoading}
                aria-label={stateIsFavorite ? "favorited" : "favorite"}
                classNames={{
                  icon: stateIsFavorite ? "text-red-9 fill-red-9" : undefined,
                }}
              >
                {stateIsFavorite ? <Trans i18nKey="common:favorited" /> : <Trans i18nKey="common:favorite" />}
              </Button>
            )}
            {[CHARACTER_VISIBILITY.PUBLIC, CHARACTER_VISIBILITY.HIDDEN].includes(character.visibility) ? (
              <CapabilityGate capability={SubCapabilityKey.CharacterShareLink}>
                <Button
                  iconStart={IconVariants.SHARE}
                  variant={ButtonVariant.TEXT}
                  onClick={() => setStateIsShareModalOpen(true)}
                  aria-label="share"
                  data-testid="GroupchatProfile-ShareButton"
                >
                  <Trans i18nKey="common:share" />
                </Button>
              </CapabilityGate>
            ) : null}
          </Row>

          <Row className="flex-wrap gap-3 opacity-60 text-sm">
            <Row className="gap-1 items-center">
              <Icon icon={IconVariants.MESSAGE} size={IconSize.SMALL} />
              <Text>{formatNumber(character?.num_messages || 0) ?? "0"}</Text>
            </Row>

            <Row className="gap-1 items-center">
              <Icon icon={IconVariants.LIKE} size={IconSize.SMALL} />
              <Text>
                {character?.rating_score && !Number.isNaN(character?.rating_score * RATING_PERCENT_MULTIPLIER)
                  ? `${Math.round(character?.rating_score * RATING_PERCENT_MULTIPLIER)}%`
                  : "0%"}
              </Text>
            </Row>

            {!isPartner && !!character?.token_count && character.token_count > 0 && (
              <LocalizedLink
                to={`${EXTERNAL_ROUTES_FOR_APPLICATION[applicationId as keyof typeof EXTERNAL_ROUTES_FOR_APPLICATION]?.docs}/advanced/tokens`}
                target="_blank"
                aria-label="tokens-info"
              >
                <Row className="gap-1 items-center">
                  <Icon icon={IconVariants.INFO} size={IconSize.SMALL} />
                  <Text className="flex items-center gap-1">
                    {formatNumber(character.token_count, true)} <Trans i18nKey="chatbot:profile.tokens" />
                  </Text>
                </Row>
              </LocalizedLink>
            )}
          </Row>
          {hasLorebookDisplayed ? (
            <div>
              {!lorebookIndicator.isInteractionDisabled ? (
                <Button
                  onClick={() => lorebookIndicator.canNavigate && navigate(`/lorebook/${lorebookIndicator.id}`)}
                  variant={ButtonVariant.SECONDARY}
                  size={ButtonSize.SMALL}
                  iconStart={lorebookIndicatorIcon}
                >
                  {lorebookIndicator.name}
                </Button>
              ) : (
                <Button variant={ButtonVariant.OUTLINED} size={ButtonSize.SMALL} iconStart={IconVariants.BOOK_LOCK} disabled>
                  <Trans i18nKey="chatbot:profile.privateLorebook" />
                </Button>
              )}
            </div>
          ) : null}
          <Text variant={TextVariants.LABEL_LARGE_REGULAR}>{character?.title}</Text>
          <TagSuggestion id={character.id} tags={character.tags} />

          <Text variant={TextVariants.LABEL_LARGE_MEDIUM}>
            <Trans i18nKey="chatbot:groupProfile.members" />
          </Text>
          <Row className="flex flex-wrap -mt-4 ml-0 gap-x-2 gap-y-[10px]">
            {character.sub_characters?.map((subCharacter) => (
              <LocalizedLink key={subCharacter.id} to={getProfilePathForCharacter(subCharacter)} aria-label={`profile-${subCharacter.name}`}>
                <AvatarChip
                  avatarSrc={
                    getAvatarUrl(subCharacter.avatar_url as string, {
                      preset: AvatarPreset.AVATAR_64X64,
                    }) ?? ""
                  }
                  name={subCharacter.name}
                  creatorUsername={subCharacter.creator_username}
                  editable={false}
                  className="cursor-pointer"
                  classNames={{
                    avatar: cn((!isAuthenticated || blurNSFW) && subCharacter.avatar_is_nsfw && "blur-md"),
                  }}
                />
              </LocalizedLink>
            ))}
          </Row>
        </Row>
      </Row>

      <TextBox
        label={t("chatbot:profile.greeting")}
        text={character?.greeting ?? ""}
        collapsed={!isDesktop}
        showMoreLabel={showMoreLabel}
        showLessLabel={showLessLabel}
      />
      {!isPageNotAccessible && (
        <>
          {character?.scenario ? (
            <TextBox
              label={t("chatbot:profile.scenario")}
              text={character?.scenario}
              collapsed={!isDesktop}
              showMoreLabel={showMoreLabel}
              showLessLabel={showLessLabel}
            />
          ) : null}
        </>
      )}
      <CapabilityShareCharacterModal
        visible={stateIsShareModalOpen}
        setShowShare={setStateIsShareModalOpen}
        characterId={characterId}
        characterName={character.name}
      />
    </Row>
  );
}
